// app/javascript/controllers/auctions_controller.js
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
    static targets = ["expandableContent"]
    static values = {
        auctionId: String
    }

    connect() {
        // Initialize sort state from form fields (which are synced with URL params)
        this.updateSortStateFromForm();
    }

    updateSortStateFromForm() {
        const form = document.getElementById('auction-filters-form');
        if (form) {
            const sortField = form.querySelector('[name="sort"]');
            const directionField = form.querySelector('[name="direction"]');
            
            this.currentSort = {
                column: sortField?.value || 'date',
                direction: directionField?.value || 'desc'
            };
        }
    }

    sort(event) {
        event.preventDefault();
        const column = event.currentTarget.dataset.auctionsColumnValue;
        
        // Update sort state from form before making changes
        this.updateSortStateFromForm();
        
        let direction = 'desc'; // Default to desc for new columns

        // If already sorted by this column, toggle direction
        if (this.currentSort.column === column) {
            direction = this.currentSort.direction === 'desc' ? 'asc' : 'desc';
        }

        // Update current sort state
        this.currentSort = { column, direction };

        // Update the shared form with sort parameters
        const form = document.getElementById('auction-filters-form');
        if (form) {
            // Add or update sort hidden fields
            this.updateSortField(form, 'sort', column);
            this.updateSortField(form, 'direction', direction);
            
            // Update URL to keep it in sync (this helps with browser back/forward)
            const url = new URL(window.location);
            url.searchParams.set('sort', column);
            url.searchParams.set('direction', direction);
            url.searchParams.set('page', '1');
            history.replaceState(null, '', url.toString());
            
            // Submit the form to trigger turbo frame update
            form.requestSubmit();
        }
    }

    updateSortField(form, name, value) {
        let field = form.querySelector(`[name="${name}"]`);
        if (!field) {
            field = document.createElement('input');
            field.type = 'hidden';
            field.name = name;
            form.appendChild(field);
        }
        field.value = value;
    }

    toggleProspects(event) {
        event.preventDefault();

        // Get the current row's auction ID from the closest parent (works for both tbody and div containers)
        const container = event.currentTarget.closest('[data-auctions-auction-id-value]');
        const auctionId = container.dataset.auctionsAuctionIdValue;
        
        // Find the expandable content and button for this specific container
        const content = container.querySelector('[data-auctions-target="expandableContent"]');
        const button = event.currentTarget;
        const icon = button.querySelector('i');

        // Check if content exists before proceeding
        if (!content) {
            console.warn('Expandable content not found for auction:', auctionId);
            return;
        }

        // Check if we're on mobile (screen width < 768px, Bootstrap's md breakpoint)
        const isMobile = window.innerWidth < 768;

        if (content.classList.contains('d-none')) {
            // Show content
            content.classList.remove('d-none');

            if (isMobile) {
                // Mobile: No animation, instant display
                content.classList.add('expanded');
                content.classList.remove('collapsed');
            } else {
                // Desktop: With animation
                setTimeout(() => {
                    content.classList.add('expanded');
                    content.classList.remove('collapsed');

                    // Scroll into view after starting animation
                    setTimeout(() => {
                        content.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                    }, 50);
                }, 10);
            }

            // Change icon if it exists
            if (icon) {
                icon.classList.remove('bi-chevron-right');
                icon.classList.add('bi-chevron-down');
            }
        } else {
            // Hide content
            if (isMobile) {
                // Mobile: No animation, instant hide - just hide it immediately
                content.classList.add('d-none');
                // Don't add/remove collapsed/expanded classes that might have transitions
            } else {
                // Desktop: With animation
                content.classList.add('collapsed');
                content.classList.remove('expanded');

                // Add d-none class after animation completes
                setTimeout(() => {
                    content.classList.add('d-none');
                }, 300);
            }

            // Change icon if it exists
            if (icon) {
                icon.classList.remove('bi-chevron-down');
                icon.classList.add('bi-chevron-right');
            }
        }
    }

    collapseProspects(event) {
        event.preventDefault();
        event.stopPropagation();

        // Get the current container
        const container = event.currentTarget.closest('[data-auctions-auction-id-value]');
        const auctionId = container.dataset.auctionsAuctionIdValue;
        
        // Find the expandable content and the toggle button
        const content = container.querySelector('[data-auctions-target="expandableContent"]');
        const toggleButton = container.querySelector('[data-action*="toggleProspects"]');
        const icon = toggleButton?.querySelector('i');

        // Check if content exists and is expanded
        if (!content || content.classList.contains('d-none')) {
            return;
        }

        // Check if we're on mobile
        const isMobile = window.innerWidth < 768;

        // Hide content
        if (isMobile) {
            // Mobile: No animation, instant hide
            content.classList.add('d-none');
        } else {
            // Desktop: With animation
            content.classList.add('collapsed');
            content.classList.remove('expanded');

            // Add d-none class after animation completes
            setTimeout(() => {
                content.classList.add('d-none');
            }, 300);
        }

        // Change icon back to chevron-right (collapsed state)
        if (icon) {
            icon.classList.remove('bi-chevron-down');
            icon.classList.add('bi-chevron-right');
        }
    }
}